import React from "react";
import { Icon } from "@/components/Icon";
import iconMark from "@/assets/pnrbet-icon.png";

/* FeaturedComposition — ONE compact art-forward hero (PREMIUM_UI_DIRECTION §5), replacing the
   old three stacked ≥240px promo banners that pushed every collection below the fold.

   ~160px tall on mobile: full-bleed game art, left-anchored scrim, eyebrow + title +
   one-line tagline + a single green Play CTA. No fabricated amounts/odds. It spotlights the
   rotating Original passed in (the lobby keeps its existing random-per-load selection), and
   on desktop expands to a lg:grid-cols-[1.6fr_1fr] pair (featured + one promo), each ≤200px.

   Props:
   - featured: { id, label, img }  (the rotating Original)
   - tagline: one-line string
   - onPlay(): open the featured game
   - promo: optional { to, eyebrow, title, subtitle, cta } for the desktop secondary card */
export default function FeaturedComposition({ featured, tagline, onPlay, promo, PromoLink }) {
  const LinkEl = PromoLink || "a";
  return (
    <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]" data-testid="casino-hero">
      {/* Featured game — the single CINEMATIC hero (BOLD_UI_DIRECTION §5): taller, a 3-gradient
          depth stack, oversized Manrope display type, and a glowing green CTA. */}
      <button
        type="button"
        onClick={onPlay}
        data-testid="casino-featured"
        className="group relative flex h-[196px] flex-col justify-end overflow-hidden rounded-2xl text-left ring-1 ring-white/[0.06] shadow-[var(--elev-3)] transition-shadow duration-[var(--dur-base)] hover:ring-white/15 lg:h-[240px] focus-ring"
      >
        <img
          src={featured?.img}
          alt={featured?.label || ""}
          className="absolute inset-0 h-full w-full object-cover object-right transition-transform duration-[var(--dur-xslow)] group-hover:scale-105 group-hover:-translate-x-1.5"
        />
        {/* 3-gradient cinematic depth stack: horizontal scrim, vertical scrim, inner edge/floor */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_1px_0_hsl(var(--edge-light)),inset_0_-40px_60px_-30px_rgba(0,0,0,0.9)]" />
        <div className="relative max-w-[80%] p-4 sm:p-5">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/75">
            <img src={iconMark} alt="" className="h-3.5 w-3.5 rounded-[3px] object-contain" /> PRYMEBET Original
          </span>
          <h3 className="mt-1 font-display text-[32px] font-black leading-[1.02] tracking-[-0.02em] text-white drop-shadow sm:text-[44px]">
            {featured?.label}
          </h3>
          <p className="mt-0.5 line-clamp-1 text-sm font-medium text-white/90 drop-shadow sm:text-base">
            {tagline || "Provably fair · play now"}
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-pnr-green px-6 py-2.5 text-sm font-extrabold text-surface-1 shadow-[var(--glow-green-soft)] transition-colors group-hover:bg-pnr-green-hover">
            <Icon name="play-circle" size={14} /> Play {featured?.label}
          </span>
        </div>
      </button>

      {/* Desktop-only secondary promo — a single compact card, never the old 3-banner stack. */}
      {promo && (
        <LinkEl
          to={promo.to}
          href={promo.to}
          className="surface-premium group relative hidden h-[240px] flex-col justify-end overflow-hidden rounded-2xl p-5 lg:flex focus-ring"
          data-testid="casino-hero-promo"
        >
          <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-70">
            <span className="absolute right-5 top-1/2 flex h-24 w-24 -translate-y-1/2 items-center justify-center rounded-full bg-pnr-green/[0.07] ring-1 ring-pnr-green/20">
              <Icon name="pnrcoin" size={30} className="text-pnr-green" />
            </span>
            <span className="absolute right-1 top-7 h-10 w-10 rounded-full bg-pnr-green/[0.06] ring-1 ring-pnr-green/15" />
            <span className="absolute bottom-8 right-24 h-8 w-8 rounded-full bg-pnr-green/[0.06] ring-1 ring-pnr-green/15" />
          </div>
          <div className="relative">
            <span className="inline-flex items-center rounded-full bg-pnr-green/15 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-pnr-green">
              {promo.eyebrow}
            </span>
            <h3 className="mt-2 font-display text-2xl font-black tracking-tight text-white">{promo.title}</h3>
            {promo.subtitle && <p className="mt-0.5 text-sm text-meta">{promo.subtitle}</p>}
            <span className="mt-4 inline-block rounded-full bg-pnr-green px-5 py-2.5 text-sm font-bold text-surface-1 transition-colors group-hover:bg-pnr-green-hover">
              {promo.cta}
            </span>
          </div>
        </LinkEl>
      )}
    </div>
  );
}
